// @ts-nocheck

import React, { useRef, useEffect } from "react";
import { useMap } from "react-leaflet";
import * as L from "leaflet";

interface Props {
  isActive: boolean;
  color: string;
  mode: "polygon" | "polyline";
  onPolygonCreated?: (polygon: L.Polygon) => void;
  onPolylineCreated?: (polyline: L.Polyline) => void;
  onModeChanged?: (mode: any) => void;
}

export default function FreeDrawWrapper({
  isActive,
  color,
  mode,
  onPolygonCreated,
  onPolylineCreated,
  onModeChanged,
}: Props) {
  const map = useMap();
  const drawingRef = useRef({
    isDrawing: false,
    path: [] as L.LatLng[],
    polyline: null as L.Polyline | null,
  });

  const isValidLatLng = (latlng: L.LatLng | undefined | null): latlng is L.LatLng =>
    !!latlng && Number.isFinite(latlng.lat) && Number.isFinite(latlng.lng);

  const getSafePath = (path: L.LatLng[]) =>
    path.filter(isValidLatLng).filter((point, index, points) => {
      if (index === 0) {
        return true;
      }

      const previousPoint = points[index - 1];
      return previousPoint.lat !== point.lat || previousPoint.lng !== point.lng;
    });

  useEffect(() => {
    if (!map) return;

    const handleMouseDown = (e: L.LeafletMouseEvent) => {
      if (!isActive || !isValidLatLng(e.latlng)) return;

      e.originalEvent.preventDefault();
      drawingRef.current.isDrawing = true;
      drawingRef.current.path = [e.latlng];

      // Disable map dragging while drawing
      map.dragging.disable();
    };

    const handleMouseMove = (e: L.LeafletMouseEvent) => {
      if (!isActive || !drawingRef.current.isDrawing || !isValidLatLng(e.latlng)) return;

      // Add point to path
      drawingRef.current.path.push(e.latlng);
      const safePath = getSafePath(drawingRef.current.path);

      // Only add the preview line after we have a real segment, and keep Geoman from snapping to it.
      if (safePath.length >= 2) {
        if (!drawingRef.current.polyline) {
          drawingRef.current.polyline = L.polyline(safePath, {
            color,
            weight: 3,
            opacity: 0.8,
            dashArray: "5, 10",
            pmIgnore: true,
            snapIgnore: true,
            interactive: false,
            bubblingMouseEvents: false,
          }).addTo(map);
        } else {
          drawingRef.current.polyline.setLatLngs(safePath);
        }
      }
    };

    const handleMouseUp = (e: L.LeafletMouseEvent) => {
      if (!isActive || !drawingRef.current.isDrawing) return;

      drawingRef.current.isDrawing = false;
      map.dragging.enable();

      // Remove the temporary polyline
      if (drawingRef.current.polyline) {
        map.removeLayer(drawingRef.current.polyline);
        drawingRef.current.polyline = null;
      }

      // Process the drawn path
      processDrawnPath();
    };

    const processDrawnPath = () => {
      const path = getSafePath(drawingRef.current.path);

      // Different minimum point requirements for polygon vs polyline
      const minPoints = mode === "polygon" ? 3 : 2;
      if (path.length < minPoints) {
        console.warn(`Free-hand drawing resulted in too few points for a ${mode}`);
        return;
      }

      // Simplify the path if it has too many points (for performance)
      let simplifiedPath = path;
      if (path.length > 100) {
        // Use a more conservative simplification tolerance
        const tempLine = L.polyline(path);
        simplifiedPath = L.LineUtil.simplify(tempLine.getLatLngs(), 0.00005);

        // If simplification was too aggressive, use a subset of the original path
        if (simplifiedPath.length < 10) {
          console.log("Simplification too aggressive, using subset of original path");
          const step = Math.floor(path.length / 50); // Take every nth point to get ~50 points
          simplifiedPath = path.filter((_, index) => index % step === 0);
          // Always include the last point
          if (simplifiedPath[simplifiedPath.length - 1] !== path[path.length - 1]) {
            simplifiedPath.push(path[path.length - 1]);
          }
        }
      }

      // Ensure we still have enough points after simplification
      if (simplifiedPath.length < minPoints) {
        console.warn(`Simplified path has too few points for a ${mode}, using original path`);
        simplifiedPath = path;
      }

      // For polygons, check if the path naturally closes (last point close to first point)
      if (mode === "polygon") {
        const firstPoint = simplifiedPath[0];
        const lastPoint = simplifiedPath[simplifiedPath.length - 1];
        const closingDistance = firstPoint.distanceTo(lastPoint);

        // If the closing distance is very small, remove the last point to avoid duplicate points
        if (closingDistance < 10 && simplifiedPath.length > 3) {
          // 10 meters threshold
          console.log("Detected natural polygon closure, removing duplicate end point");
          simplifiedPath = simplifiedPath.slice(0, -1);
        }
      }

      // console.log("Final path has", simplifiedPath.length, "points");

      if (mode === "polygon") {
        // Create a polygon from the simplified path
        const polygon = L.polygon(simplifiedPath, {
          color: color,
          weight: 3,
          fillColor: color,
          fillOpacity: 0.3,
        });

        // Add to map temporarily to get proper GeoJSON structure
        polygon.addTo(map);

        // Call the callback with the created polygon
        onPolygonCreated?.(polygon);
      } else {
        // Create a polyline from the simplified path
        const polyline = L.polyline(simplifiedPath, {
          color: color,
          weight: 3,
          opacity: 1,
        });

        // Add to map temporarily to get proper GeoJSON structure
        polyline.addTo(map);

        // Call the callback with the created polyline
        onPolylineCreated?.(polyline);
      }

      // Reset drawing state
      drawingRef.current.path = [];
    };

    // Touch event handlers for mobile support
    const handleTouchStart = (e: TouchEvent) => {
      if (!isActive) return;

      // Prevent any default touch behaviors
      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      if (touch) {
        const rect = map.getContainer().getBoundingClientRect();
        const latlng = map.containerPointToLatLng([
          touch.clientX - rect.left,
          touch.clientY - rect.top,
        ]);
        handleMouseDown({ latlng, originalEvent: e } as L.LeafletMouseEvent);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isActive || !drawingRef.current.isDrawing) return;

      // Prevent any default touch behaviors and scrolling
      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      if (touch) {
        const rect = map.getContainer().getBoundingClientRect();
        const point = map.containerPointToLatLng([
          touch.clientX - rect.left,
          touch.clientY - rect.top,
        ]);
        handleMouseMove({ latlng: point } as L.LeafletMouseEvent);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isActive || !drawingRef.current.isDrawing) return;

      // Prevent any default touch behaviors
      e.preventDefault();
      e.stopPropagation();

      handleMouseUp({} as L.LeafletMouseEvent);
    };

    // Add event listeners when active
    if (isActive) {
      console.log("FreeDrawWrapper activated"); // Debug log

      // Add CSS class for enhanced free-draw mode styling and touch control
      map.getContainer().classList.add("free-draw-active");

      // Disable map dragging and zoom during free drawing
      map.dragging.disable();
      map.scrollWheelZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoom.disable();

      map.on("mousedown", handleMouseDown);
      map.on("mousemove", handleMouseMove);
      map.on("mouseup", handleMouseUp);

      // Touch events for mobile with enhanced prevention
      map.getContainer().addEventListener("touchstart", handleTouchStart, { passive: false });
      map.getContainer().addEventListener("touchmove", handleTouchMove, { passive: false });
      map.getContainer().addEventListener("touchend", handleTouchEnd, { passive: false });
    } else {
      // Remove CSS class when not active
      map.getContainer().classList.remove("free-draw-active");

      // Re-enable map interactions
      map.dragging.enable();
      map.scrollWheelZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoom.enable();
    }

    // Cleanup function
    return () => {
      map.off("mousedown", handleMouseDown);
      map.off("mousemove", handleMouseMove);
      map.off("mouseup", handleMouseUp);

      // Remove touch event listeners with correct options
      map.getContainer().removeEventListener("touchstart", handleTouchStart);
      map.getContainer().removeEventListener("touchmove", handleTouchMove);
      map.getContainer().removeEventListener("touchend", handleTouchEnd);

      // Remove CSS class and restore map interactions
      map.getContainer().classList.remove("free-draw-active");

      // Re-enable all map interactions
      map.dragging.enable();
      map.scrollWheelZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoom.enable();

      // Clean up any temporary drawing
      if (drawingRef.current.polyline) {
        map.removeLayer(drawingRef.current.polyline);
        drawingRef.current.polyline = null;
      }

      // Reset drawing state
      drawingRef.current.isDrawing = false;
      drawingRef.current.path = [];
    };
  }, [map, isActive, color, mode, onPolygonCreated, onPolylineCreated]);

  // This component doesn't render anything visible - it only handles events
  return null;
}
